import { useMemo } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { initialMunichCityPassPassenger, munichCityPassActionCreators } from "../munichCityPassModule";
// helpers
import { getUpdatedArray } from "../../../../common/utils/helpers/getUpdatedArray";
import { getPassengerFieldErrors } from "../helpers/getPassengerFieldErrors";
import { type MunichCityPassPassenger } from "../types";
// components
import AppDatePickerCustom from "../../../../components/AppDatePickerCustom";
import {
  PassengerAddButton,
  PassengerColumnHeaders,
  PassengerFormShell,
  PassengerNameFields,
  PassengerRemoveButton,
  PassengerRowMobileDivider
} from "../../shared/passengers";

const MUNICH_CITY_PASS_MAX_PASSENGERS = 5;

export default function MunichCityPassPassengersForm() {
  const { t } = useTranslation();

  const passengerList = useSelector((state: any) => state.munichCityPass.passengerList) as MunichCityPassPassenger[];
  const showValidationErrors = useSelector((state: any) => state.munichCityPass.showValidationErrors);
  const prebookingError = useSelector((state: any) => state.munichCityPass.prebookingError);

  const updatePassengers = useActions(munichCityPassActionCreators.updatePassengersAction, []);
  const clearPrebookingError = useActions(munichCityPassActionCreators.clearPrebookingErrorAction, []);

  const { byPassenger: passengerFieldErrors } = useMemo(
    () => getPassengerFieldErrors(prebookingError, passengerList.length),
    [prebookingError, passengerList.length]
  );

  // A passenger change makes passenger field errors stale; "See price" validates again.
  // Other errors (5xx, missing IATA, offer-level) are kept, as before.
  const clearStalePrebookingError = () => {
    if (Object.keys(passengerFieldErrors).length > 0) {
      clearPrebookingError();
    }
  };

  const handlePassengerFirstNameChange = (event: any, index: number) => {
    updatePassengers(getUpdatedArray(passengerList, index, "firstName", event.target.value));
    clearStalePrebookingError();
  };

  const handlePassengerLastNameChange = (event: any, index: number) => {
    updatePassengers(getUpdatedArray(passengerList, index, "lastName", event.target.value));
    clearStalePrebookingError();
  };

  const handleBirthdateChange = (date: Date | null, index: number) => {
    if (date == null) {
      return;
    }
    updatePassengers(getUpdatedArray(passengerList, index, "birthDate", date));
    clearStalePrebookingError();
  };

  const removePassenger = (index: number) => {
    updatePassengers(passengerList.filter((_, i) => i !== index));
    clearStalePrebookingError();
  };

  const addPassengerToList = () => {
    if (passengerList.length >= MUNICH_CITY_PASS_MAX_PASSENGERS) {
      return;
    }

    updatePassengers([...passengerList, { ...initialMunichCityPassPassenger, hasRemove: true }]);
    clearStalePrebookingError();
  };

  return (
    <PassengerFormShell
      title={`${t("transport.passengers")} *`}
      columnHeaders={
        <PassengerColumnHeaders>
          <Grid item xs={4} className="passengers-subtitle" pb={1}>
            {t("transport.formFirstName")}
          </Grid>
          <Grid item xs={4} className="passengers-subtitle" pb={1}>
            {t("transport.formLastName")}
          </Grid>
          <Grid item xs={4} className="passengers-subtitle" pb={1}>
            {t("transport.formBirthdate")}
          </Grid>
        </PassengerColumnHeaders>
      }
      addButton={
        passengerList.length < MUNICH_CITY_PASS_MAX_PASSENGERS ? (
          <PassengerAddButton onClick={addPassengerToList} />
        ) : null
      }
    >
      {passengerList?.length > 0 &&
        passengerList.map((passenger, index) => {
          const shouldHighlightFirstName = showValidationErrors && (passenger?.firstName?.length ?? 0) < 2;
          const shouldHighlightLastName = showValidationErrors && (passenger?.lastName?.length ?? 0) < 2;
          const fieldErrors = passengerFieldErrors[index];
          const fieldErrorMessages = [fieldErrors?.firstName, fieldErrors?.lastName, fieldErrors?.birthDate].filter(
            Boolean
          );

          return (
            <Grid container columnSpacing={{ xs: 1 }} mb={2} key={index}>
              <PassengerNameFields
                firstName={passenger.firstName}
                lastName={passenger.lastName}
                firstNameField="firstName"
                lastNameField="lastName"
                md={4}
                onFirstNameChange={(event: any) => handlePassengerFirstNameChange(event, index)}
                onLastNameChange={(event: any) => handlePassengerLastNameChange(event, index)}
                firstNameError={shouldHighlightFirstName || !!fieldErrors?.firstName}
                lastNameError={shouldHighlightLastName || !!fieldErrors?.lastName}
              />
              <Grid item md={4} sm={12} xs={12}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    alignItems: "center",
                    justifyContent: { sm: "space-between", md: "flex-start" }
                  }}
                >
                  <Box
                    data-cy={`munich-city-pass-birthdate-wrapper-${index}`}
                    sx={{ width: "100%" }}
                    className={fieldErrors?.birthDate ? "munich-city-pass__birthdate--invalid" : undefined}
                  >
                    <AppDatePickerCustom
                      selectedDate={passenger.birthDate}
                      setSelectedDate={date => handleBirthdateChange(date, index)}
                      dataAttribute={`munich-city-pass-birthdate-${index}`}
                    />
                    <Box sx={{ display: { sm: "flex", md: "none", fontSize: "11px" } }} mt={0.5} ml={0.5}>
                      {t("transport.formBirthdate")}
                    </Box>
                  </Box>
                  <Grid
                    item
                    ml={{ xs: 0, sm: 0, md: 3 }}
                    mt={{ xs: 2.5, sm: 2.5, md: 0 }}
                    sx={{
                      display: { xs: "flex", sm: "flex", md: "block" },
                      width: { xs: "100%", sm: "100%", md: "auto" },
                      ...(passenger?.hasRemove ? {} : { opacity: 0, pointerEvents: "none" })
                    }}
                  >
                    <PassengerRemoveButton onClick={() => removePassenger(index)} />
                  </Grid>
                </Grid>
              </Grid>
              {fieldErrorMessages.length > 0 && (
                // All backend messages for this passenger, grouped on the left under the row (first name column)
                <Grid item xs={12} data-cy={`munich-city-pass-passenger-errors-${index}`}>
                  {fieldErrorMessages.map((message, messageIndex) => (
                    <div key={messageIndex} className="munich-city-pass__field-error">
                      {message}
                    </div>
                  ))}
                </Grid>
              )}
              <PassengerRowMobileDivider />
            </Grid>
          );
        })}
    </PassengerFormShell>
  );
}
